/**
 * Services - Convenient shortcuts for ServiceRegistry access
 * 
 * Eliminates verbose boilerplate:
 *   const registry = ServiceRegistry.getInstance();
 *   const entitySystem = registry.get('EntitySystem');
 *   const playerManager = entitySystem?.getPlayerManager?.();
 *   const player = playerManager?.getPlayer?.();
 * 
 * Becomes:
 *   const player = Services.player();
 * 
 * Or for multiple services:
 *   const { entitySystem, mapSystem, physicsSystem } = Services.all();
 */

import { ServiceRegistry } from './ServiceRegistry';
import type { EntitySystem } from '../systems/entity/EntitySystem';
import type { MapSystem } from '../systems/map/MapSystem';
import type { PhysicsSystem } from '../systems/physics/PhysicsSystem';
import type { InputSystem } from '../systems/input/InputSystem';
import type { CombatSystem } from '../systems/combat/CombatSystem';
import type { PlayerManager } from '../systems/player/PlayerManager';
import type { ProjectileSystem } from '../systems/projectile/ProjectileSystem';
import type { NPCManager } from '../systems/npc/NPCManager';
import type { CameraSystem } from '../systems/camera/CameraSystem';
import { VFXSystem } from '../effects/VFXSystem';
import type { CastTimelineSystem } from '../systems/combat/CastTimelineSystem';
import type { InventorySystem } from '../systems/inventory/InventorySystem';
import type { QuestSystem } from '../systems/quest/QuestSystem';
import type { PhysicsCoordinator } from '../systems/physics/PhysicsCoordinator';

// Registry singleton accessor
const registry = () => ServiceRegistry.getInstance();

/**
 * Core Systems - Most commonly accessed
 */
export const Services = {
  /**
   * Get EntitySystem (central entity management)
   */
  entity(): EntitySystem | undefined {
    return registry().get('EntitySystem');
  },

  /**
   * Get MapSystem (map loading, switching, teleporters)
   */
  map(): MapSystem | undefined {
    return registry().get('MapSystem');
  },

  /**
   * Get PhysicsSystem (physics coordinator wrapper)
   */
  physics(): PhysicsSystem | undefined {
    return registry().get('PhysicsSystem');
  },

  /**
   * Get PhysicsCoordinator directly (for physics operations)
   */
  physicsCoordinator(): PhysicsCoordinator | undefined {
    return this.physics()?.getCoordinator();
  },

  /**
   * Get InputSystem (keyboard/mouse input)
   */
  input(): InputSystem | undefined {
    return registry().get('InputSystem');
  },

  /**
   * Get CombatSystem (combat mechanics)
   */
  combat(): CombatSystem | undefined {
    return registry().get('CombatSystem');
  },

  /**
   * Get VFXSystem (visual effects)
   */
  vfx(): VFXSystem | undefined {
    // VFXSystem is a scene-coupled SINGLETON, not a ServiceRegistry entry.
    // getInstance() returns it once the scene has created it (undefined before
    // first scene init — callers already guard). This finishes the cleanup the
    // prior fix (86561d61) documented but deferred: registry.get('VFXSystem')
    // always missed → Services.vfx() consumers silently no-oped.
    return VFXSystem.getInstance();
  },

  /**
   * Get CameraSystem (camera following, effects)
   */
  camera(): CameraSystem | undefined {
    return registry().get('CameraSystem');
  },
};

/**
 * Player-related shortcuts
 */
export const PlayerServices = {
  /**
   * Get PlayerManager (player state, character management)
   */
  manager(): PlayerManager | null | undefined {
    return Services.entity()?.getPlayerManager?.();
  },

  /**
   * Get current player entity
   */
  entity() {
    return this.manager()?.getPlayer?.();
  },

  /**
   * Get player sprite (rendered sprite handle)
   */
  sprite(): any | null | undefined {
    return this.manager()?.getPlayerSprite?.();
  },

  /**
   * Get player position {x, y}
   */
  position(): { x: number; y: number } | null | undefined {
    const player = this.entity();
    return player?.position;
  },

  /**
   * Check if player exists and is valid
   */
  exists(): boolean {
    return !!this.entity();
  },
};

/**
 * Combat & Ability shortcuts
 */
export const CombatServices = {
  /**
   * Get CastTimelineSystem (ability casting animations)
   */
  timeline(): CastTimelineSystem | undefined {
    return registry().get('CastTimelineSystem');
  },

  /**
   * Get ProjectileSystem
   */
  projectiles(): ProjectileSystem | null | undefined {
    return Services.entity()?.getProjectileSystem?.();
  },
};

/**
 * NPC shortcuts
 */
export const EntityServices = {
  /**
   * Get NPCManager
   */
  npcs(): NPCManager | null | undefined {
    return Services.entity()?.getNPCManager?.();
  },
};

/**
 * Map & World shortcuts
 */
export const WorldServices = {
  /**
   * Get current level/map ID
   */
  currentMapId(): string | undefined {
    return Services.map()?.getCurrentLevel?.()?.id;
  },

  /**
   * Check if a map is loaded
   */
  isMapLoaded(): boolean {
    return !!Services.map()?.getCurrentLevel?.();
  },
};

/**
 * Inventory & Quest shortcuts
 */
export const GameServices = {
  /**
   * Get InventorySystem
   */
  inventory(): InventorySystem | undefined {
    return registry().get('InventorySystem');
  },

  /**
   * Get QuestSystem
   */
  quests(): QuestSystem | undefined {
    return registry().get('QuestSystem');
  },
};

/**
 * All services in one object (for destructuring)
 * Usage: const { entitySystem, mapSystem, physicsSystem } = Services.all();
 */
export function useGameServices() {
  return {
    // Core systems
    entitySystem: Services.entity(),
    mapSystem: Services.map(),
    physicsSystem: Services.physics(),
    physicsCoordinator: Services.physicsCoordinator(),
    inputSystem: Services.input(),
    combatSystem: Services.combat(),
    vfxSystem: Services.vfx(),
    cameraSystem: Services.camera(),

    // Player
    playerManager: PlayerServices.manager(),
    player: PlayerServices.entity(),
    playerSprite: PlayerServices.sprite(),

    // Combat
    timelineSystem: CombatServices.timeline(),
    projectileSystem: CombatServices.projectiles(),

    // Entities
    npcManager: EntityServices.npcs(),

    // Game
    inventorySystem: GameServices.inventory(),
    questSystem: GameServices.quests(),
  };
}

// Re-export everything from Services namespace for convenience
export default {
  ...Services,
  player: PlayerServices.entity,
  playerManager: PlayerServices.manager,
  playerSprite: PlayerServices.sprite,
  playerPosition: PlayerServices.position,
  hasPlayer: PlayerServices.exists,
  all: useGameServices,
};
